import {observer} from "mobx-react";
import {useHistory, useParams} from "react-router-dom";
import {Drawer} from "../Drawer/Drawer";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {AppStoreContext} from "../../Stores/AppStore";
import {FormRef, GroupItem, NumericRule, RequiredRule, SimpleItem} from "devextreme-react/form";
import {MaintenanceDTO, MaintenanceStatus, TaskType} from "../../Models/ApiModels";
import DateTimeService from "../../Services/DateTimeService";
import styles from "./Service.module.scss";
import {Button, Form, Tabs} from "devextreme-react";
import {AppRouting, TEXTAREA_MAX_LENGTH} from "../../AppConstants";
import {FieldDataChangedEvent} from "devextreme/ui/form";

enum TasksTabs {
    Info = 'Info',
    Complete = 'Complete'
}

const getDuration = (hoursPerWeek: number, totalHours: number) => {
    const durationHours = (7 * 24 / hoursPerWeek) * totalHours;
    const days = Math.floor(durationHours / 24);
    const hours = Math.round(durationHours % 24);
    return { days, hours };
}

const TasksDetails = observer(() => {
    const context = useContext(AppStoreContext);
    const params: { id: string } = useParams();
    const history = useHistory();
    const _formRef = useRef<FormRef>(null);
    const [task, setTask] = useState<MaintenanceDTO | null>(null);
    const [taskType, setTaskType] = useState<string>(TaskType.data);
    const [timeCode, setTimeCode] = useState<string>('P0DT0H');
    const [tab, setTab] = useState<TasksTabs>(TasksTabs.Info);
    const {
        checklist, measurements,
        filtersStore, loadTasks, loadMeasurements, loadComponents,
        convertToTicket, saveTask
    } = context;

    useEffect(() => {
        if (checklist === null) {
            void loadTasks();
        }
        if (measurements === null) {
            void loadMeasurements();
        }
    }, [checklist, measurements, filtersStore.selected, loadTasks, loadMeasurements]);

    useEffect(() => {
        if (checklist !== null) {
            let edited = checklist.find(t => t.taskId === params.id);
            if (params.id !== 'new' && !edited) {
                history.push(`${AppRouting.Tasks}/new`);
            }
            if (!edited) {
                edited = {
                    type: filtersStore.isIoTSubscription ? TaskType.data : TaskType.scheduled,
                } as MaintenanceDTO;
                setTaskType(filtersStore.isIoTSubscription ? TaskType.data : TaskType.scheduled);
            } else {
                edited = {...edited, ...{componentId: measurements?.find(e => e.id === edited?.measurement)?.componentId || ''}};
                const isScheduled = !!edited.timeDelta;
                edited.type = isScheduled ? TaskType.scheduled : TaskType.data;
                if (!isScheduled) {
                    void loadComponents(filtersStore.selected.plant);
                } else {
                    const [D, H] = edited.timeDelta.split('T');
                    edited.timeD = D ? +D.replaceAll(/\D/g, '') : 0;
                    edited.timeH = H ? +H.replaceAll(/\D/g, '') : 0;
                }
                setTaskType(isScheduled ? TaskType.scheduled : TaskType.data);
            }
            setTask(edited);
            void loadComponents(edited.deviceId);
        }
    }, [params.id, checklist, history, measurements, loadComponents, filtersStore.selected, filtersStore.isIoTSubscription]);

    const isCompleteDialog = task?.status === MaintenanceStatus.Completed;

    const _convertToTicket = useCallback(() => {
        if (task) {
            history.push(`${AppRouting.Tickets}/new`, {task});
            convertToTicket(task);
        }
    }, [task, history, convertToTicket]);


    const toTicketAction = (
        <Button
            type="default"
            text="Convert to Ticket"
            disabled={isCompleteDialog}
            className={styles.mlAuto}
            onClick={_convertToTicket}
        />
    );

    const _onClose = useCallback(() => {
        setTask(null);
        history.push(AppRouting.Tasks);
    }, [history, setTask]);

    const _onSave = async () => {
        const validateRes = _formRef.current?.instance().validate();
        if (validateRes?.isValid && task) {
            try {
                await saveTask(task);
                _onClose();
            } catch (e) {

            }
        }
    };

    const _onDelete = useCallback((taskId: string, taskName: string) => {
        context.confirmationStore.showConfirmation(`Do you really want to delete the task "${taskName}"?`, 'Delete task').then(async () => {
            await context.deleteTask(taskId);
            _onClose();
            void context.loadTasks();
        });
    }, [context, _onClose]);

    const _onFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
        if (e.dataField === 'isManualDuration') {

            setTask(
                {...(task as MaintenanceDTO),
                    isManualDuration: e.value,
                }
            );
        }
        if ((e.dataField === 'operatingHours' && task?.operatingHoursPerWeek) || (e.dataField === 'operatingHoursPerWeek' && task?.operatingHours)) {
            const {days, hours} = getDuration( task?.operatingHoursPerWeek??0,  task?.operatingHours??0)

            setTask(
                {...task,
                    timeH: hours,
                    timeD: days,
                }
            );
        }

        if (e.dataField === 'type') {
            setTaskType(e.value);
        }
        if (e.dataField === 'timeH') {
            const [D] = timeCode.split('T');
            const newCode = `${D}T${e.value}H`;
            setTimeCode(newCode);
            if(task?.operatingHoursPerWeek || task?.operatingHours){
                setTask(
                    {...task,
                        operatingHours: undefined,
                        operatingHoursPerWeek: undefined,
                    }
                );
            }
        }
        if (e.dataField === 'timeD') {
            const [, H] = timeCode.split('T');
            const newCode = `P${e.value}DT${H}`;
            setTimeCode(newCode);
            if(task?.operatingHoursPerWeek || task?.operatingHours){
                setTask(
                    {...task,
                        operatingHours: undefined,
                        operatingHoursPerWeek: undefined,
                    }
                );
            }
        }
    }, [timeCode, task]);

    const onSelectionChanged = useCallback((args) => {
        let origin = checklist?.find(t => t.taskId === params.id);
        const tab: TasksTabs = args.selectedItem || args.addedItems[0];
        setTab(tab);
        if (origin && task) {
            setTask({
                ...task, ...{
                    status: tab === TasksTabs.Complete ? MaintenanceStatus.Completed : origin.status,
                }
            });
        }
    }, [setTab, task, setTask, checklist, params.id]);

    const addStylesToElement = useCallback((counter: HTMLElement) => {
        counter.style.textAlign = "right";
        counter.style.fontSize = "11px";
        counter.style.marginTop = "4px";
        counter.style.color = "#888";
        counter.style.position = "absolute";
        counter.style.bottom = "0px";
        counter.style.right = "5px";
    },[]);

    return (
        <Drawer
            title={task?.taskId ? "Task Details" : "New Task"}
            titleAction={task?.taskId && !isCompleteDialog ? toTicketAction : undefined}
            onClose={_onClose}
            saveAction={{
                onClick: _onSave,
                text: `${task?.taskId ? isCompleteDialog ? "Complete" : "Save" : "Create"} Task`,
            }}
            closeAction={{
                onClick: _onClose,
                text: 'Close',
            }}
            deleteAction={task?.taskId ? {
                onClick: () =>  _onDelete(task.taskId, task?.description??''),
                text: 'Delete',
            } : undefined}
        >
            <Tabs
                visible={!!task?.taskId}
                className={styles.tabs}
                dataSource={[TasksTabs.Info, TasksTabs.Complete]}
                scrollingEnabled={false}
                onSelectionChanged={onSelectionChanged}
                selectedItem={tab}
            />
            <Form
                formData={task}
                labelLocation="top"
                onFieldDataChanged={_onFieldDataChanged}
                ref={_formRef}>

                <SimpleItem
                    dataField="description"
                    editorType="dxTextArea"
                    colSpan={1}
                    visible={tab === TasksTabs.Info}
                    editorOptions={{
                        maxLength: TEXTAREA_MAX_LENGTH,
                        height: 100,
                        disabled: isCompleteDialog,
                        valueChangeEvent: "input",
                        onInitialized: (e: any) => {
                            const editor = e.component;
                            const element = editor.element();
                            const counter = document.createElement("div");
                            addStylesToElement(counter);
                            element.append(counter);

                            const value = editor.option("value") || "";
                            counter.innerText = `${value.length}/${TEXTAREA_MAX_LENGTH}`;
                            editor.__counter = counter;
                        },

                        onValueChanged: (e: any) => {
                            const counter = e.component.__counter;
                            if (counter) {
                                counter.innerText = `${(e.value?.length ?? 0)}/${TEXTAREA_MAX_LENGTH}`;
                            }
                        }
                    }}
                    label={{text: 'Description'}}>
                    <RequiredRule message="Description is required"/>
                </SimpleItem>

                <SimpleItem
                    dataField="type"
                    editorType="dxSelectBox"
                    visible={tab === TasksTabs.Info}
                    label={{text: 'Task Type'}}
                    editorOptions={{
                        disabled: task?.taskId || isCompleteDialog || !filtersStore.isIoTSubscription,
                        items: filtersStore.isIoTSubscription ?
                            [TaskType.data, TaskType.scheduled] : [TaskType.scheduled]
                    }}
                />
                {taskType === TaskType.data && tab === TasksTabs.Info && (
                    <>
                        <SimpleItem
                            dataField="measurement"
                            editorType="dxSelectBox"
                            label={{text: 'Count Type'}}
                            editorOptions={{
                                items: measurements || [],
                                displayExpr: "name",
                                valueExpr: "id",
                                disabled: task?.taskId || isCompleteDialog,
                            }}
                        >
                            <RequiredRule message="Count Type is required"/>
                        </SimpleItem>

                        <SimpleItem
                            dataField="valueDelta"
                            editorOptions={{
                                disabled: task?.taskId || isCompleteDialog,
                            }}
                            label={{text: 'Metric Count'}}>
                            <RequiredRule message="Metric is required"/>
                        </SimpleItem>
                    </>
                )}
                { !(task?.taskId || isCompleteDialog) && <SimpleItem
                    dataField="isManualDuration"
                    editorType="dxCheckBox"
                    label={{ text: "Enter duration manually" }}
                    editorOptions={{
                        value: task?.isManualDuration || false,
                        disabled: task?.taskId || isCompleteDialog,
                    }}
                />
                }
                { !(task?.taskId || isCompleteDialog) &&
                    <GroupItem cssClass="form-group" colCount={2}
                               visible={!task?.isManualDuration}>
                        <GroupItem>
                            <SimpleItem
                                dataField="operatingHoursPerWeek"
                                editorType="dxNumberBox"
                                label={{text: 'Operating hours per week'}}
                                visible={taskType === TaskType.scheduled}
                                editorOptions={{
                                    disabled: task?.taskId || isCompleteDialog,
                                }}
                            >
                                <NumericRule message="Invalid value"/>
                            </SimpleItem>
                        </GroupItem>
                        <GroupItem>
                            <SimpleItem
                                dataField="operatingHours"
                                editorType="dxNumberBox"
                                label={{text: 'Total operating hours'}}
                                visible={taskType === TaskType.scheduled}
                                editorOptions={{
                                    disabled: task?.taskId || isCompleteDialog,
                                }}
                            >
                                <NumericRule message="Invalid value"/>
                            </SimpleItem>
                        </GroupItem>
                    </GroupItem>

                }
                {taskType === TaskType.scheduled && tab === TasksTabs.Info && (
                    <>
                        <GroupItem cssClass="form-group" colCount={2}>
                            <GroupItem>
                                <SimpleItem
                                    dataField="timeD"
                                    editorType="dxNumberBox"
                                    label={{text: 'Days'}}
                                    visible={taskType === TaskType.scheduled}
                                    editorOptions={{
                                        disabled: task?.taskId || isCompleteDialog,
                                        acceptCustomValue: true,
                                        readOnly: !task?.isManualDuration,
                                    }}
                                >
                                    <RequiredRule message="Days is required"/>
                                    <NumericRule message="Invalid value"/>
                                </SimpleItem>
                            </GroupItem>
                            <GroupItem>
                                <SimpleItem
                                    dataField="timeH"
                                    editorType="dxNumberBox"
                                    label={{text: 'Hours'}}
                                    visible={taskType === TaskType.scheduled}
                                    editorOptions={{
                                        disabled: task?.taskId || isCompleteDialog,
                                        readOnly: !task?.isManualDuration,
                                    }}
                                >
                                    <NumericRule message="Invalid value"/>
                                    <RequiredRule message="Hours is required"/>
                                </SimpleItem>
                            </GroupItem>
                        </GroupItem>
                        <SimpleItem
                            dataField="initialTime"
                            editorType="dxDateBox"
                            editorOptions={{
                                type: "datetime",
                                disabled: task?.taskId || isCompleteDialog,
                                displayFormat: "EEE MMM dd yyyy HH:mm",
                            }}
                            label={{text: `Start Date ${task?.taskId ? '' : '(Default in now)'}`}}>
                        </SimpleItem>
                        <SimpleItem
                          dataField="dueDate"
                          editorType="dxDateBox"
                          editorOptions={{
                              type: "datetime",
                              disabled: task?.taskId || isCompleteDialog,
                              displayFormat: "EEE MMM dd yyyy HH:mm",
                          }}
                          label={{text: `Due Date`}}>
                        </SimpleItem>
                    </>
                )}
                <SimpleItem
                    dataField="isRecurring"
                    label={{text: 'Recurring'}}
                    visible={tab === TasksTabs.Info}
                    editorOptions={{
                        disabled: isCompleteDialog,
                    }}
                    editorType="dxCheckBox"/>

                <SimpleItem
                    visible={tab === TasksTabs.Complete && taskType === TaskType.data}
                    dataField="value"
                    label={{text: `Recorded Metric (recorded at  ${DateTimeService.formatLocal(DateTimeService.now(), "DD.MM.YYYY HH:mm")})`}}>
                    <RequiredRule message="Recorded Metric is required"/>
                </SimpleItem>

                <SimpleItem
                    dataField="log"
                    editorType="dxTextArea"
                    colSpan={1}
                    visible={tab === TasksTabs.Complete}
                    editorOptions={{
                        height: 100,
                        maxLength: TEXTAREA_MAX_LENGTH,
                        disabled: isCompleteDialog,
                        valueChangeEvent: "input",
                        onInitialized: (e: any) => {
                            const editor = e.component;
                            const element = editor.element();
                            const counter = document.createElement("div");
                            addStylesToElement(counter);
                            element.append(counter);

                            const value = editor.option("value") || "";
                            counter.innerText = `${value.length}/${TEXTAREA_MAX_LENGTH}`;

                            editor.__counter = counter;
                        },

                        onValueChanged: (e: any) => {
                            const counter = e.component.__counter;
                            if (counter) {
                                counter.innerText = `${(e.value?.length ?? 0)}/${TEXTAREA_MAX_LENGTH}`;
                            }
                        }
                    }}
                    label={{text: 'Add Notes'}}>
                    <RequiredRule message="Notes is required"/>
                </SimpleItem>

            </Form>
        </Drawer>
    );
});
export default TasksDetails;